import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Package, TrendingUp, Clock, Gauge, Factory, Calendar, AlertTriangle } from "lucide-react";
import { calculateUPM, calculateUptimeUPM, getYieldColor, formatYield, aggregateLotQuantities } from "@/utils/productionCalculations";
import DowntimeBreakdown from "./DowntimeBreakdown";

const CompletedWorkOrderSummary = ({ workOrder, items }) => {
  const [showDowntimeModal, setShowDowntimeModal] = useState(false);
  const item = items?.find(i => i.item_id === workOrder.item_id);
  const unitsPerCase = item?.units_per_case || 12;
  
  const upm = calculateUPM(
    workOrder.actual_quantity,
    unitsPerCase,
    workOrder.actual_start_time,
    workOrder.actual_end_time
  );
  
  const uptimeUpm = calculateUptimeUPM(
    workOrder.actual_quantity,
    unitsPerCase,
    workOrder.actual_start_time,
    workOrder.actual_end_time,
    workOrder.downtime_minutes
  );

  const efficiency = workOrder.planned_quantity > 0
    ? ((workOrder.actual_quantity / workOrder.planned_quantity) * 100).toFixed(1)
    : 0;

  return (
    <div className="completed-wo-summary">
      {/* KPI Cards Grid - Condensed */}
      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
        {/* Production Details */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
              <Factory className="w-3.5 h-3.5" />
              Line
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold">{workOrder.production_line}</div>
          </CardContent>
        </Card>

        {/* Production Date */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Date
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-sm font-semibold">{workOrder.actual_date || workOrder.planned_date}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {workOrder.actual_start_time} - {workOrder.actual_end_time}
            </div>
          </CardContent>
        </Card>

        {/* Quantity */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
              <Package className="w-3.5 h-3.5" />
              Quantity
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold">{workOrder.actual_quantity} <span className="text-xs font-normal text-gray-500">cs</span></div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Plan: {workOrder.planned_quantity}
            </div>
          </CardContent>
        </Card>

        {/* Efficiency */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              Efficiency
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className={`text-xl font-bold ${efficiency >= 95 ? 'text-green-600' : efficiency >= 85 ? 'text-orange-600' : 'text-red-600'}`}>
              {efficiency}%
            </div>
          </CardContent>
        </Card>

        {/* UPM */}
        <Card className="hover:shadow-md transition-shadow" style={{ background: '#f0f9ff', borderColor: '#3b82f6' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-blue-700 flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5" />
              UPM
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold text-blue-600">{upm || 'N/A'}</div>
            <div className="text-[10px] text-blue-600 mt-0.5">Units/min</div>
          </CardContent>
        </Card>

        {/* Uptime UPM */}
        <Card className="hover:shadow-md transition-shadow" style={{ background: '#f0fdf4', borderColor: '#22c55e' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-green-700 flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5" />
              Uptime UPM
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold text-green-600">{uptimeUpm || 'N/A'}</div>
            <div className="text-[10px] text-green-600 mt-0.5">w/o downtime</div>
          </CardContent>
        </Card>

        {/* Downtime - Clickable */}
        <Card 
          className="hover:shadow-lg transition-all cursor-pointer border-2"
          style={{ background: '#fff7ed', borderColor: '#fb923c' }}
          onClick={(e) => {
            e.stopPropagation();
            console.log('Downtime card clicked, opening modal');
            setShowDowntimeModal(true);
          }}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-amber-700 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Downtime
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="text-xl font-bold text-amber-600">
              {workOrder.downtime_minutes || 0} <span className="text-xs font-normal text-gray-500">min</span>
            </div>
            <div className="text-[10px] text-amber-600 mt-0.5">
              {workOrder.downtime_entries?.length || 0} events • Click for details
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Downtime Breakdown Modal */}
      <DowntimeBreakdown 
        isOpen={showDowntimeModal}
        onClose={() => setShowDowntimeModal(false)}
        downtimeEntries={workOrder.downtime_entries}
        totalDowntime={workOrder.downtime_minutes || 0}
      />

      <Separator className="my-4" />

      {/* BOM Summary - Consolidated by Component with Lot Breakdown */}
      <div className="bom-summary">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
          <Package className="w-4 h-4" />
          Bill of Materials Summary
        </h3>

        {workOrder.bom_usage && workOrder.bom_usage.length > 0 ? (
          <div className="space-y-3">
            {workOrder.bom_usage.map((component, index) => {
              const yieldColor = getYieldColor(component.yield_pct, component.item_type);
              const isFood = component.item_type === 'food';
              const displayUnit = isFood && component.unit === 'g' ? 'lbs' : component.unit;
              
              // Aggregate lot quantities
              const { totalAllocated, totalActual } = component.lots && component.lots.length > 0
                ? aggregateLotQuantities(component.lots)
                : { totalAllocated: 0, totalActual: component.actual_qty };

              // All quantities are now in proper units (lbs for food, units for packaging)
              const plannedDisplay = component.planned_qty;
              const actualDisplay = totalActual;
              const wasteDisplay = component.waste_qty || 0;

              return (
                <Card key={index} className="bom-summary-card">
                  <CardHeader className="pb-2 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        {component.component_name}
                        <Badge variant="outline" className="text-[10px] ml-1 px-1.5 py-0">
                          {component.item_type}
                        </Badge>
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-[10px] text-gray-500">Yield</div>
                          <div 
                            className="text-base font-bold flex items-center gap-1"
                            style={{ 
                              color: yieldColor === 'green' ? '#16a34a' : yieldColor === 'orange' ? '#ea580c' : '#dc2626'
                            }}
                          >
                            <TrendingUp className="w-3.5 h-3.5" />
                            {formatYield(component.yield_pct)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-3 pb-3">
                    {/* Lot-by-Lot Breakdown */}
                    {component.lots && component.lots.length > 0 ? (
                      <div className="mb-3">
                        <div className="text-[10px] font-semibold text-gray-600 mb-1.5">LOT USAGE:</div>
                        <div className="space-y-1.5">
                          {component.lots.map((lot, lotIndex) => {
                            // All quantities are now in proper units
                            const lotActualDisplay = lot.actual_qty;
                            const lotAllocatedDisplay = lot.allocated_qty;
                            
                            return (
                              <div key={lotIndex} className="flex justify-between items-center bg-gray-50 p-2 rounded border">
                                <div>
                                  <div className="font-mono text-xs font-semibold text-gray-700">{lot.lot_number}</div>
                                  <div className="text-[10px] text-gray-500 mt-0.5">
                                    Alloc: {lotAllocatedDisplay} {displayUnit}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs font-semibold text-gray-900">
                                    {lotActualDisplay} {displayUnit}
                                  </div>
                                  <div className="text-[10px] text-gray-500">Used</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {/* Totals Row */}
                    <Separator className="my-2" />
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">Planned</div>
                        <div className="text-sm font-semibold">{plannedDisplay} {displayUnit}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">Actual</div>
                        <div className="text-sm font-semibold text-blue-600">{actualDisplay} {displayUnit}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">Waste</div>
                        <div className="text-sm font-semibold text-red-600 flex items-center justify-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {wasteDisplay} {displayUnit}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">Net</div>
                        <div className="text-xs text-gray-600">
                          {(totalActual + (component.waste_qty || 0)).toFixed(2)} {displayUnit}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No BOM components recorded</p>
          </div>
        )}
      </div>

      {/* Notes */}
      {workOrder.notes && (
        <>
          <Separator className="my-3" />
          <div>
            <h4 className="text-xs font-semibold text-gray-600 mb-1.5">Notes</h4>
            <p className="text-xs text-gray-700">{workOrder.notes}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default CompletedWorkOrderSummary;
